import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError, timer } from 'rxjs';
import { catchError, retryWhen, mergeMap } from 'rxjs/operators';
import { SocketService } from './socket.service';

@Injectable({
    providedIn: 'root'
})
export class MediaService {

    // No leading slash: ApiBaseInterceptor only rewrites paths that start with 'api/' to
    // environment.API_URL. With '/api/media' the native app resolved this against its own
    // WebView origin (capacitor://localhost/api/media/...) and got back index.html, which is
    // what broke recording on iOS 0.0.973 once the worker-first path was removed.
    private apiUrl = 'api/media';
    private socketId: string = '';

    private socketSubs: Subscription[] = [];

    private transcriptionStatusSubject = new BehaviorSubject<any>(null);
    transcriptionStatus$ = this.transcriptionStatusSubject.asObservable();

    private transcriptionProgressSubject = new BehaviorSubject<any>(null);
    transcriptionProgress$ = this.transcriptionProgressSubject.asObservable();

    constructor(private http: HttpClient, private socketService: SocketService) {
        this.socketService.reconnected$.subscribe(() => {
            if (this.socketId) {
                this.socketService.emit('joinTranscriptionWorker', { socketId: this.socketId });
            }
        });
    }

    private getAuthHeaders(): HttpHeaders {
        const token = localStorage.getItem('token');
        return new HttpHeaders().set('Authorization', `Bearer ${token}`);
    }

    resetStatus() {
        this.socketId = '';
        this.transcriptionStatusSubject.next(null);
        this.transcriptionProgressSubject.next(null);
    }

    // Both media calls go to the main API only. They used to try the transcription worker first
    // (environment.TRANSCRIPTION_WORKER_URL) and fall back here on error, but the worker's copies of
    // these routes had no authentication and were removed (pentest F-024, 2026-09-04). The main API
    // authenticates the caller and checks patient ownership before minting the SAS; the worker only
    // consumes the transcription queue, so nothing is lost by enqueueing via the main API.
    getUploadUrl(patientId: string, jobId: string, fileName: string): Observable<any> {
        const body = { patientId, jobId, fileName };
        return this.http.post(`${this.apiUrl}/get-upload-url`, body, {
            headers: this.getAuthHeaders()
        }).pipe(
            retryWhen(this.genericRetryStrategy()),
            catchError(this.handleError('getUploadUrl'))
        );
    }

    uploadFileToAzure(file: File, uploadUrl: string): Observable<any> {
        return this.http.put(uploadUrl, file, {
            headers: new HttpHeaders({
                'x-ms-blob-type': 'BlockBlob',
                'Content-Type': file.type
            })
        }).pipe(
            retryWhen(this.genericRetryStrategy()),
            catchError(this.handleError('uploadFileToAzure'))
        );
    }

    notifyServerFileUploaded(jobId: string, socketId: string, blobName: string, containerName: string, patientId: string, groupId: string, userId: string, jobType: 'full' | 'segment'): Observable<any> {
        const body = { jobId, socketId, blobName, containerName, patientId, groupId, userId, jobType };
        return this.http.post(`${this.apiUrl}/queue-transcription`, body, {
            headers: this.getAuthHeaders()
        }).pipe(
            retryWhen(this.genericRetryStrategy()),
            catchError(this.handleError('notifyServerFileUploaded'))
        );
    }

    subscribeToTranscriptionResults(jobId: string) {
        console.log('listening for transcription worker responses on: ', jobId);
        this.socketId = jobId;
        this.socketService.emit('joinTranscriptionWorker', { socketId: jobId });

        if (this.socketSubs) {
            for (const sub of this.socketSubs)
                sub.unsubscribe();
        }

        this.socketSubs.push(
            this.socketService.listen('transcriptionCompleted', 'media-service')
                .subscribe(output => {
                    console.log('transcriptionCompleted', output);
                    this.transcriptionStatusSubject.next(output);
                })
        );

        this.socketSubs.push(
            this.socketService.listen('transcriptionProgress', 'media-service')
                .subscribe(output => {
                    console.log('transcriptionProgress', output);
                    this.transcriptionProgressSubject.next(output);
                })
        );
    }

    // Generic Retry Logic with Exponential Backoff
    private genericRetryStrategy = ({
        maxRetryAttempts = 5,
        scalingDuration = 1000,
        excludedStatusCodes = [400, 401, 403, 404]
    } = {}) => (attempts: Observable<any>) => {
        return attempts.pipe(
            mergeMap((error, i) => {
                const retryAttempt = i + 1;
                if (
                    retryAttempt > maxRetryAttempts ||
                    excludedStatusCodes.includes(error.status)
                ) {
                    return throwError(error);
                }
                const delayDuration = scalingDuration * Math.pow(2, i);
                console.warn(
                    `Attempt ${retryAttempt}: retrying in ${delayDuration / 1000}s`
                );
                return timer(delayDuration);
            })
        );
    };

    private handleError(operation: string) {
        return (error: any) => {
            console.error(`${operation} failed:`, error);
            return throwError(error);
        };
    }
}
